/**
 * Ministry Shop — the public storefront, in the FWM house style.
 * Same header, cream ground, serif display type, gold buttons and dark footer
 * as the rest of the public platform. Browse by category, basket in
 * localStorage, guest checkout, ministry pricing applied automatically when
 * the buyer's email is on a church roll.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, ArrowRight, Check, Download, Loader2, Minus, Package, Plus,
  Search, ShoppingBag, Trash2, Truck,
} from 'lucide-react';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY, FWM_CARD_ART, resolveImage } from '@/components/fwm/imagery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;
const BASKET_KEY = 'ministry_shop_basket';
const EMAIL_KEY = 'ministry_shop_email';
const CURRENCY_KEY = 'ministry_shop_currency';
const TOKEN_KEY = 'ministry_shop_basket_token';

const money = (value, currency = 'USD') => {
  const symbols = { USD: '$', GBP: '£', EUR: '€', ZAR: 'R' };
  const symbol = symbols[currency];
  return symbol ? `${symbol}${Number(value || 0).toFixed(2)}`
                : `${currency} ${Number(value || 0).toFixed(2)}`;
};

export const readCurrency = () => localStorage.getItem(CURRENCY_KEY) || '';
const writeCurrency = (code) => {
  if (code) localStorage.setItem(CURRENCY_KEY, code);
  else localStorage.removeItem(CURRENCY_KEY);
  window.dispatchEvent(new Event('shop-currency'));
};

export const readBasket = () => {
  try {
    return JSON.parse(localStorage.getItem(BASKET_KEY) || '[]')
      .map((i) => ({ mode: 'buy', ...i }));
  } catch { return []; }
};
const writeBasket = (items) => {
  localStorage.setItem(BASKET_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event('shop-basket'));
};

export const useBasket = () => {
  const [items, setItems] = useState(readBasket);
  useEffect(() => {
    const sync = () => setItems(readBasket());
    window.addEventListener('shop-basket', sync);
    return () => window.removeEventListener('shop-basket', sync);
  }, []);
  const add = (product, qty = 1, mode = 'buy') => {
    const next = readBasket();
    const single = product.kind !== 'physical' || mode === 'rent';
    const found = next.find((i) => i.product_id === product.id && i.mode === mode);
    if (found) {
      if (single) return toast.info('Already in your basket');
      found.qty += qty;
    } else {
      next.push({ product_id: product.id, qty: single ? 1 : qty, mode });
    }
    writeBasket(next);
    toast.success(mode === 'rent' ? `${product.name} added as a 7-day rental`
                                  : `${product.name} added`);
    return null;
  };
  const setQty = (productId, qty, mode = 'buy') => {
    const next = readBasket().map((i) => (
      i.product_id === productId && i.mode === mode
        ? { ...i, qty: Math.max(1, qty) } : i));
    writeBasket(next);
  };
  const remove = (productId, mode = 'buy') => writeBasket(
    readBasket().filter((i) => !(i.product_id === productId && i.mode === mode)));
  const clear = () => writeBasket([]);
  const replace = (rows) => writeBasket((rows || []).map(
    (i) => ({ product_id: i.product_id, qty: i.qty || 1, mode: i.mode || 'buy' })));
  const count = items.reduce((sum, i) => sum + i.qty, 0);
  return { items, add, setQty, remove, clear, replace, count };
};

const CurrencyPicker = ({ currencies, current }) => {
  if (!currencies || currencies.length < 2) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="shop-currency-picker">
      {currencies.map((c) => (
        <button key={c.code} onClick={() => writeCurrency(c.code)}
                data-testid={`shop-currency-${c.code}`}
                data-active={current === c.code}
                className="fwm-chip !px-2.5 !py-1 !text-[0.6875rem]">
          {c.code}
        </button>
      ))}
    </div>
  );
};

/** The slim strip that carries the shop's own controls under the FWM header. */
const ShopBar = ({ basketCount, currencies, currency }) => (
  <div className="fwm-subbar" data-testid="shop-bar">
    <div className="mx-auto max-w-[1180px] px-6 lg:px-10 h-14 flex items-center gap-5">
      <Link to="/shop" data-testid="shop-home-link"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase
                       tracking-[0.16em] text-[color:var(--fwm-ink)]">
        <ShoppingBag className="h-3.5 w-3.5 text-[color:var(--fwm-accent)]" /> Ministry Shop
      </Link>
      <div className="ml-auto flex items-center gap-5">
        <CurrencyPicker currencies={currencies} current={currency} />
        <Link to="/shop/checkout" data-testid="shop-basket-link"
              className="inline-flex items-center gap-2 text-sm font-medium
                         text-[color:var(--fwm-ink)]">
          Basket
          <span className="min-w-6 h-6 px-1.5 grid place-items-center rounded-full
                           bg-[color:var(--fwm-accent)] text-[color:var(--fwm-charcoal)]
                           text-xs font-bold tabular-nums"
                data-testid="shop-basket-count">{basketCount}</span>
        </Link>
      </div>
    </div>
  </div>
);

const ShopShell = ({ children, hero, basketCount, currencies, currency,
                     testId = 'shop-page' }) => (
  <FwmShell ctaLabel="Basket" ctaLink="/shop/checkout" testId={testId} overDark={!!hero}>
    {hero}
    <ShopBar basketCount={basketCount} currencies={currencies} currency={currency} />
    {children}
  </FwmShell>
);

const KindLabel = { bundle: 'Bundle', digital: 'Digital', physical: 'Physical' };

const ProductCard = ({ product, onAdd, currency, index = 0 }) => {
  const art = resolveImage(product.image_url)
    || FWM_CARD_ART[index % FWM_CARD_ART.length];
  return (
    <article className="fwm-photo-card fwm-rise flex flex-col"
             style={{ animationDelay: `${index * 55}ms` }}
             data-testid={`shop-product-${product.id}`}>
      <Link to={`/shop/product/${product.slug}`} className="fwm-photo-card-hit"
            data-testid={`shop-product-link-${product.id}`}>
        <span className="fwm-photo-card-media">
          <img src={art} alt={product.name} loading="lazy" />
          <span className="fwm-photo-card-overline">{product.category_label}</span>
          {product.is_bestseller && (
            <span className="fwm-photo-card-badge" data-testid={`shop-bestseller-${product.id}`}>
              Bestseller
            </span>
          )}
        </span>
        <span className="fwm-photo-card-body !pb-4">
          <span className="fwm-photo-card-title">{product.name}</span>
          {product.author && (
            <span className="mt-2 text-xs uppercase tracking-[0.14em]
                             text-[color:var(--fwm-muted)]">{product.author}</span>
          )}
          {product.short_description && (
            <span className="fwm-photo-card-blurb">{product.short_description}</span>
          )}
        </span>
      </Link>

      <div className="px-6 pb-6 mt-auto">
        <div className="flex flex-wrap items-center gap-2">
          <span className="fwm-badge">{KindLabel[product.kind] || 'Physical'}</span>
          {product.bundle_saving > 0 && (
            <span className="fwm-badge !border-[color:var(--fwm-navy)]
                             !text-[color:var(--fwm-navy)]">
              Save {money(product.bundle_saving, currency)}
            </span>
          )}
          {product.publisher_source === 'abmb' && <span className="fwm-badge">ABMB</span>}
        </div>
        <div className="mt-4 flex items-end justify-between gap-4">
          <div>
            <p className="fwm-price" data-testid={`shop-price-${product.id}`}>
              {money(product.your_price, currency)}
            </p>
            {product.member_price < product.price && (
              <p className="mt-1 text-[11px] text-[color:var(--fwm-muted)]">
                members {money(product.member_price, currency)}
              </p>
            )}
            {product.rental_enabled && (
              <p className="mt-1 text-[11px] text-[color:var(--fwm-muted)]"
                 data-testid={`shop-rent-note-${product.id}`}>
                rent {product.rental_days} days {money(product.rental_price, currency)}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 items-stretch">
            <button disabled={!product.in_stock} onClick={() => onAdd(product)}
                    data-testid={`shop-add-${product.id}`}
                    className="fwm-btn !py-2.5 !px-5 !text-[0.8125rem]
                               disabled:opacity-40 disabled:cursor-not-allowed">
              {product.in_stock ? 'Add' : 'Sold out'}
            </button>
            {product.rental_enabled && (
              <button onClick={() => onAdd(product, 1, 'rent')}
                      data-testid={`shop-rent-${product.id}`}
                      className="fwm-btn-ghost !py-2 !px-5 !text-[0.75rem]">
                Rent
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const basket = useBasket();
  const [q, setQ] = useState('');
  const category = params.get('category') || '';
  const email = localStorage.getItem(EMAIL_KEY) || '';
  const [currencyPick, setCurrencyPick] = useState(readCurrency);
  useEffect(() => {
    const sync = () => setCurrencyPick(readCurrency());
    window.addEventListener('shop-currency', sync);
    return () => window.removeEventListener('shop-currency', sync);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['shop-products', category, q, email, currencyPick],
    queryFn: () => fetch(`${API}/public/products?${new URLSearchParams({
      ...(category ? { category } : {}), ...(q ? { q } : {}), ...(email ? { email } : {}),
      ...(currencyPick ? { currency: currencyPick } : {}),
    })}`).then((r) => r.json()),
  });

  const { data: shelf } = useQuery({
    queryKey: ['shop-bestsellers', currencyPick, email],
    queryFn: () => fetch(`${API}/public/bestsellers?${new URLSearchParams({
      window: 'month', limit: 4, ...(currencyPick ? { currency: currencyPick } : {}),
      ...(email ? { email } : {}),
    })}`).then((r) => r.json()),
  });

  const products = data?.products || [];
  const currency = data?.currency || 'USD';
  const shopHero = useHero('shop', { image_url: FWM_IMAGERY.teaching });

  const hero = (
    <FwmHero
      testId="shop-hero"
      image={shopHero.image_url}
      video={shopHero.video_url}
      mediaPriority={shopHero.media_priority}
      eyebrow={shopHero.eyebrow}
      title={shopHero.title}
      accent={shopHero.accent}
      blurb={shopHero.blurb}
      actions={(
        <>
          <button className="fwm-btn fwm-btn-lg" data-testid="shop-browse-btn"
                  onClick={() => document.getElementById('catalogue')
                    ?.scrollIntoView({ behavior: 'smooth' })}>
            Browse the shelves <ArrowRight className="h-4 w-4" />
          </button>
          <Link to="/shop/checkout" className="fwm-btn-ondark" data-testid="shop-hero-basket">
            <ShoppingBag className="h-4 w-4" /> Your basket
          </Link>
        </>
      )}
    />
  );

  return (
    <ShopShell hero={hero} basketCount={basket.count} currencies={data?.currencies}
               currency={currency}>
      {data?.is_member && (
        <p className="mx-auto max-w-[1180px] px-6 lg:px-10 pt-8 text-sm font-semibold
                      text-[color:var(--fwm-accent-dark)]" data-testid="shop-member-note">
          Ministry pricing is on for {email}
        </p>
      )}

      {(shelf?.products || []).length > 0 && !category && !q && (
        <section className="fwm-section !pb-0" data-testid="shop-bestseller-shelf">
          <div className="fwm-section-head">
            <div>
              <p className="fwm-kicker">Most bought this month</p>
              <h2 className="mt-3">Off the shelf fastest</h2>
            </div>
            <p className="text-sm text-[color:var(--fwm-muted)]">
              Updates itself as orders come in
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {shelf.products.map((p, i) => (
              <ProductCard key={`bs-${p.id}`} product={p} currency={currency}
                           index={i} onAdd={basket.add} />
            ))}
          </div>
        </section>
      )}

      <section id="catalogue" className="fwm-section" data-testid="shop-catalogue">
        <div className="fwm-section-head">
          <div>
            <p className="fwm-kicker">The catalogue</p>
            <h2 className="mt-3">{q ? `Matching "${q}"` : 'Everything on the shelves'}</h2>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5
                               text-[color:var(--fwm-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search the shop" data-testid="shop-search"
                   className="fwm-field !pl-9" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button onClick={() => setParams({})} data-testid="shop-category-all"
                  data-active={!category} className="fwm-chip">
            Everything
          </button>
          {(data?.categories || []).map((c) => (
            <button key={c.key} onClick={() => setParams({ category: c.key })}
                    data-testid={`shop-category-${c.key}`}
                    data-active={category === c.key} className="fwm-chip">
              {c.label}
              <span className="fwm-chip-count">{data?.counts?.[c.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 rounded-xl bg-[color:var(--fwm-ground-2)] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="mt-12 border border-[color:var(--fwm-line)] rounded-xl px-8 py-16
                          text-center" data-testid="shop-empty">
            <ShoppingBag className="h-8 w-8 mx-auto text-[color:var(--fwm-muted)]" />
            <p className="mt-5 text-[color:var(--fwm-body)]">
              Nothing is listed here yet — the shop is being stocked.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
               data-testid="shop-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} currency={currency} index={i}
                           onAdd={basket.add} />
            ))}
          </div>
        )}
      </section>
    </ShopShell>
  );
}

export function ShopProductPage() {
  const { slug } = useParams();
  const basket = useBasket();
  const navigate = useNavigate();
  const email = localStorage.getItem(EMAIL_KEY) || '';
  const [qty, setQty] = useState(1);
  const [currencyPick, setCurrencyPick] = useState(readCurrency);
  useEffect(() => {
    const sync = () => setCurrencyPick(readCurrency());
    window.addEventListener('shop-currency', sync);
    return () => window.removeEventListener('shop-currency', sync);
  }, []);

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['shop-product', slug, email, currencyPick],
    queryFn: () => fetch(`${API}/public/products/${slug}?${new URLSearchParams({
      ...(email ? { email } : {}), ...(currencyPick ? { currency: currencyPick } : {}),
    })}`).then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); }),
    retry: false,
  });

  if (isLoading) {
    return (
      <ShopShell basketCount={basket.count} testId="shop-product-loading">
        <div className="fwm-section">
          <div className="h-96 rounded-xl bg-[color:var(--fwm-ground-2)] animate-pulse" />
        </div>
      </ShopShell>
    );
  }
  if (error || !product) {
    return (
      <ShopShell basketCount={basket.count}>
        <div className="fwm-section text-center" data-testid="shop-product-missing">
          <p className="text-[color:var(--fwm-body)]">That product is not in the shop.</p>
          <button className="fwm-btn mt-8" onClick={() => navigate('/shop')}>
            Back to the shop <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </ShopShell>
    );
  }

  const art = resolveImage(product.image_url);

  return (
    <ShopShell basketCount={basket.count} currencies={product.currencies}
               currency={product.currency}>
      <main className="mx-auto max-w-[1180px] px-6 lg:px-10 py-16"
            data-testid="shop-product-page">
        <Link to="/shop"
              className="text-xs uppercase tracking-[0.16em] text-[color:var(--fwm-muted)]
                         hover:text-[color:var(--fwm-ink)] inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> All products
        </Link>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div className="fwm-photo-card !shadow-none overflow-hidden">
            {art ? (
              <img src={art} alt={product.name}
                   className="w-full h-[460px] object-cover" />
            ) : (
              <div className="w-full h-[460px] grid place-items-center
                              bg-[color:var(--fwm-ground-2)]">
                <Package className="h-10 w-10 text-[color:var(--fwm-muted)]" />
              </div>
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="fwm-badge">{product.category_label}</span>
              <span className="fwm-badge-solid">
                {product.kind === 'bundle' ? 'Bundle'
                  : product.kind === 'digital' ? 'Digital download' : 'Physical item'}
              </span>
              {product.publisher_source === 'abmb'
                ? <span className="fwm-badge">ABMB</span>
                : product.publisher_name
                  ? <span className="fwm-badge">{product.publisher_name}</span>
                  : null}
            </div>
            <h1 className="fwm-display mt-6 text-4xl sm:text-5xl leading-[1.08] tracking-tight"
                data-testid="shop-product-name">{product.name}</h1>
            {product.author && (
              <p className="mt-3 text-xs uppercase tracking-[0.16em]
                            text-[color:var(--fwm-muted)]">{product.author}</p>
            )}
            <p className="fwm-price mt-7 !text-3xl" data-testid="shop-product-price">
              {money(product.your_price, product.currency)}
            </p>
            {product.member_price < product.price && (
              <p className="mt-2 text-xs text-[color:var(--fwm-muted)]">
                Members pay {money(product.member_price, product.currency)} — use the email
                on your church record at checkout.
              </p>
            )}
            {product.description && (
              <p className="mt-7 text-[color:var(--fwm-body)] leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            )}
            <div className="mt-8 space-y-2 text-xs text-[color:var(--fwm-muted)]">
              {product.kind === 'physical' && (
                <>
                  <p className="inline-flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5" />
                    {product.in_stock ? `${product.stock} in stock` : 'Out of stock'}
                    {product.shipping_note ? ` · ${product.shipping_note}` : ''}
                  </p>
                  {product.dimensions && <p>{product.dimensions}</p>}
                </>
              )}
              {product.kind === 'digital' && (
                <p className="inline-flex items-center gap-1.5">
                  <Download className="h-3.5 w-3.5" />
                  {product.file_format || 'File'} download link straight after payment
                </p>
              )}
              {product.isbn && <p>ISBN {product.isbn}</p>}
            </div>

            {product.kind === 'bundle' && (product.contents || []).length > 0 && (
              <div className="mt-8 fwm-card p-6" data-testid="shop-bundle-contents">
                <p className="fwm-kicker">What&apos;s inside</p>
                <ul className="mt-4 space-y-2.5">
                  {product.contents.map((c) => (
                    <li key={c.product_id} className="flex justify-between text-sm">
                      <Link to={`/shop/product/${c.slug}`}
                            className="hover:text-[color:var(--fwm-accent-dark)]">
                        {c.name}{c.qty > 1 ? ` × ${c.qty}` : ''}
                      </Link>
                      <span className="text-[color:var(--fwm-muted)]">
                        {money(c.price, product.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 pt-4 border-t border-[color:var(--fwm-line)] text-sm">
                  Bought separately {money(product.bundle_value, product.currency)} —
                  <span className="font-semibold text-[color:var(--fwm-navy)]">
                    {' '}you save {money(product.bundle_saving, product.currency)}
                  </span>
                </p>
              </div>
            )}

            {product.rental_enabled && (
              <div className="mt-8 rounded-xl border border-[color:var(--fwm-accent)]
                              bg-[color:var(--fwm-ground-2)] p-6"
                   data-testid="shop-rental-panel">
                <p className="fwm-display text-lg">
                  Rent it for {product.rental_days} days —
                  {' '}{money(product.rental_price, product.currency)}
                </p>
                <p className="mt-2 text-xs text-[color:var(--fwm-body)] leading-relaxed">
                  Full access for {product.rental_days} days from payment, then the link
                  quietly expires. Buy it outright any time to keep it.
                </p>
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-3">
              {product.kind === 'physical' && (
                <div className="flex items-center border border-[color:var(--fwm-strong)]
                                rounded-[3px]">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2.5"
                          data-testid="shop-qty-minus"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="px-4 text-sm font-medium tabular-nums"
                        data-testid="shop-qty">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-3 py-2.5"
                          data-testid="shop-qty-plus"><Plus className="h-3.5 w-3.5" /></button>
                </div>
              )}
              <button disabled={!product.in_stock} onClick={() => basket.add(product, qty)}
                      data-testid="shop-product-add"
                      className="fwm-btn fwm-btn-lg disabled:opacity-40
                                 disabled:cursor-not-allowed">
                {product.in_stock ? 'Add to basket' : 'Sold out'}
              </button>
              {product.rental_enabled && (
                <button onClick={() => basket.add(product, 1, 'rent')}
                        data-testid="shop-product-rent" className="fwm-btn-ghost fwm-btn-lg">
                  Rent {product.rental_days} days · {money(product.rental_price,
                    product.currency)}
                </button>
              )}
              <button onClick={() => navigate('/shop/checkout')}
                      data-testid="shop-product-checkout" className="fwm-btn-ghost fwm-btn-lg">
                Checkout <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {(product.related || []).length > 0 && (
          <section className="mt-24 border-t border-[color:var(--fwm-line)] pt-14">
            <div className="fwm-section-head !mb-8">
              <h2>More in {product.category_label}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {product.related.map((r, i) => (
                <ProductCard key={r.id} product={r} currency={product.currency}
                             index={i} onAdd={basket.add} />
              ))}
            </div>
          </section>
        )}
      </main>
    </ShopShell>
  );
}

export function ShopCheckoutPage() {
  const basket = useBasket();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [form, setForm] = useState(() => ({
    name: '', email: localStorage.getItem(EMAIL_KEY) || '', phone: '',
    address: '', city: '', country: '', postal_code: '', notes: '',
    payment_method: '', wallet: '', discount_code: search.get('code') || '',
    is_gift: false, gift_wrap: false, gift_note: '',
    recipient_name: '', recipient_email: '',
  }));
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paynowInfo, setPaynowInfo] = useState(null);
  const currencyPick = readCurrency();
  const restoreToken = search.get('basket');

  // Came in from a "you left something behind" email — hand the basket back.
  useEffect(() => {
    if (!restoreToken) return;
    fetch(`${API}/public/basket/${restoreToken}`).then((r) => r.json())
      .then((data) => {
        if (!data?.items?.length) return;
        basket.replace(data.items);
        localStorage.setItem(TOKEN_KEY, restoreToken);
        setForm((f) => ({ ...f, email: data.email || f.email,
                          name: data.name || f.name }));
        toast.success('Your basket is back');
      })
      .catch(() => toast.error('That basket link has expired'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreToken]);

  const { data: settings } = useQuery({
    queryKey: ['shop-settings'],
    queryFn: () => fetch(`${API}/public/settings`).then((r) => r.json()),
  });

  const methods = useMemo(() => {
    const pm = settings?.payment_methods || {};
    return [
      ['stripe', 'Card', pm.stripe],
      ['paynow', 'EcoCash / OneMoney / InnBucks', pm.paynow],
      ['bank_transfer', 'Bank transfer', pm.bank_transfer],
    ].filter(([, , on]) => on);
  }, [settings]);

  useEffect(() => {
    if (!form.payment_method && methods.length) {
      setForm((f) => ({ ...f, payment_method: methods[0][0] }));
    }
  }, [methods, form.payment_method]);

  useEffect(() => {
    if (!basket.items.length) { setQuote(null); return; }
    fetch(`${API}/public/quote`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: basket.items, name: form.name || 'Guest',
                             email: form.email, payment_method: 'stripe',
                             currency: currencyPick,
                             gift: { is_gift: form.is_gift, wrap: form.gift_wrap },
                             discount_code: form.discount_code }),
    }).then((r) => r.json()).then(setQuote).catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket.items, form.email, form.name, form.discount_code, form.gift_wrap]);

  // Keep the basket server-side as soon as we have an email, so it can be
  // handed back later (and nudged if the order never happens).
  useEffect(() => {
    if (!form.email.includes('@') || !basket.items.length) return;
    const timer = setTimeout(() => {
      fetch(`${API}/public/basket`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: localStorage.getItem(TOKEN_KEY) || '',
          email: form.email, name: form.name, items: basket.items,
          currency: currencyPick, channel: 'app',
          origin_url: window.location.origin,
        }),
      }).then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data?.token) localStorage.setItem(TOKEN_KEY, data.token); })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, form.name, basket.items]);

  const place = async () => {
    if (!form.name.trim() || !form.email.includes('@')) {
      return toast.error('We need your name and a working email');
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/orders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: basket.items, name: form.name, email: form.email, phone: form.phone,
          payment_method: form.payment_method, wallet: form.wallet,
          discount_code: form.discount_code,
          shipping: { address: form.address, city: form.city, country: form.country,
                      postal_code: form.postal_code, notes: form.notes },
          origin_url: window.location.origin,
          currency: currencyPick,
          basket_token: localStorage.getItem(TOKEN_KEY) || '',
          gift: {
            is_gift: form.is_gift, wrap: form.gift_wrap,
            note: form.gift_note, recipient_name: form.recipient_name,
            recipient_email: form.recipient_email,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not place the order');
      localStorage.setItem(EMAIL_KEY, form.email);
      localStorage.removeItem(TOKEN_KEY);
      basket.clear();
      if (data.payment?.kind === 'stripe' && data.payment.url) {
        window.location.href = data.payment.url;
        return;
      }
      if (data.payment?.kind === 'paynow') {
        setPaynowInfo({ ...data.payment, order: data.order });
        return;
      }
      navigate(`/shop/order/${data.order.order_number}?email=${encodeURIComponent(form.email)}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (paynowInfo) {
    return (
      <ShopShell basketCount={0}>
        <main className="mx-auto max-w-[640px] px-6 py-24 text-center"
              data-testid="shop-paynow-panel">
          <p className="fwm-kicker">Almost there</p>
          <h1 className="fwm-display mt-4 text-4xl">Approve it on your phone</h1>
          <p className="mt-5 text-[color:var(--fwm-body)] leading-relaxed">
            {paynowInfo.instructions}
          </p>
          <button className="fwm-btn fwm-btn-lg mt-10" data-testid="shop-paynow-continue"
                  onClick={() => navigate(`/shop/order/${paynowInfo.order.order_number}?email=${encodeURIComponent(form.email)}`)}>
            I have approved it <ArrowRight className="h-4 w-4" />
          </button>
        </main>
      </ShopShell>
    );
  }

  return (
    <ShopShell basketCount={basket.count}>
      <main className="mx-auto max-w-[1180px] px-6 lg:px-10 py-16" data-testid="shop-checkout">
        <p className="fwm-kicker">Checkout</p>
        <h1 className="fwm-display mt-4 text-4xl sm:text-5xl tracking-tight">Your basket</h1>

        {basket.items.length === 0 ? (
          <div className="mt-12 border border-[color:var(--fwm-line)] rounded-xl px-8 py-16
                          text-center" data-testid="shop-basket-empty">
            <ShoppingBag className="h-8 w-8 mx-auto text-[color:var(--fwm-muted)]" />
            <p className="mt-5 text-[color:var(--fwm-body)]">Your basket is empty.</p>
            <button className="fwm-btn mt-8" onClick={() => navigate('/shop')}>
              Browse the shop <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3 space-y-4">
              {(quote?.items || []).map((line) => (
                <div key={`${line.product_id}-${line.mode || 'buy'}`}
                     className="fwm-card p-4 flex items-center gap-4"
                     data-testid={`basket-line-${line.product_id}`}>
                  {line.image_url && (
                    <img src={resolveImage(line.image_url)} alt={line.name}
                         className="h-16 w-16 object-cover rounded-[3px]" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{line.name}</p>
                    <p className="text-xs text-[color:var(--fwm-muted)]">
                      {money(line.unit_price, quote?.currency)} each
                      {line.mode === 'rent'
                        ? ` · ${line.rental_days}-day rental` : ''}
                      {line.kind === 'bundle' ? ' · bundle' : ''}
                      {line.unit_price < line.list_price && ' · ministry price'}
                    </p>
                  </div>
                  {line.kind === 'physical' && line.mode !== 'rent' && (
                    <div className="flex items-center border border-[color:var(--fwm-strong)]
                                    rounded-[3px]">
                      <button className="px-2 py-1" data-testid={`basket-minus-${line.product_id}`}
                              onClick={() => basket.setQty(line.product_id, line.qty - 1,
                                                           line.mode || 'buy')}>
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="px-3 text-sm tabular-nums">{line.qty}</span>
                      <button className="px-2 py-1" data-testid={`basket-plus-${line.product_id}`}
                              onClick={() => basket.setQty(line.product_id, line.qty + 1,
                                                           line.mode || 'buy')}>
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <p className="w-20 text-right font-semibold tabular-nums">
                    {money(line.line_total, quote?.currency)}
                  </p>
                  <button onClick={() => basket.remove(line.product_id, line.mode || 'buy')}
                          data-testid={`basket-remove-${line.product_id}`}
                          className="text-[color:var(--fwm-muted)] hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <div className="fwm-card p-7 space-y-5">
                <h2 className="fwm-display text-xl">Where do we send it?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Your name *</Label>
                    <Input value={form.name} data-testid="checkout-name"
                           onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email *</Label>
                    <Input value={form.email} data-testid="checkout-email"
                           onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Phone</Label>
                    <Input value={form.phone} data-testid="checkout-phone"
                           onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  {quote?.needs_shipping && (
                    <>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">Delivery address *</Label>
                        <Input value={form.address} data-testid="checkout-address"
                               onChange={(e) => setForm({ ...form, address: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">City</Label>
                        <Input value={form.city} data-testid="checkout-city"
                               onChange={(e) => setForm({ ...form, city: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Country</Label>
                        <Input value={form.country} data-testid="checkout-country"
                               onChange={(e) => setForm({ ...form, country: e.target.value })} />
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Anything we should know?</Label>
                    <Textarea rows={2} value={form.notes} data-testid="checkout-notes"
                              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>

                  {quote?.needs_shipping && (
                    <div className="sm:col-span-2 rounded-xl border border-[color:var(--fwm-line)]
                                    bg-[color:var(--fwm-ground-2)] p-5 space-y-4"
                         data-testid="checkout-gift">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.is_gift}
                               data-testid="checkout-is-gift"
                               onChange={(e) => setForm({
                                 ...form, is_gift: e.target.checked,
                                 gift_wrap: e.target.checked && form.gift_wrap })}
                               className="h-4 w-4 accent-[color:var(--fwm-accent)]" />
                        <span className="text-sm font-medium">This is a gift</span>
                      </label>
                      {form.is_gift && (
                        <div className="space-y-4 pl-7">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={form.gift_wrap}
                                   data-testid="checkout-gift-wrap"
                                   onChange={(e) => setForm({ ...form,
                                     gift_wrap: e.target.checked })}
                                   className="h-4 w-4 accent-[color:var(--fwm-accent)]" />
                            <span className="text-sm">
                              Wrap it with a ribbon
                              {quote?.gift_wrap_fee > 0
                                ? ` (${money(quote.gift_wrap_fee, quote?.currency)})`
                                : ''}
                            </span>
                          </label>
                          <div className="space-y-1.5">
                            <Label className="text-xs">
                              A short note — we write it on the card
                            </Label>
                            <Textarea rows={3} maxLength={400} value={form.gift_note}
                                      data-testid="checkout-gift-note"
                                      placeholder="Thinking of you as you start this new season…"
                                      onChange={(e) => setForm({ ...form,
                                        gift_note: e.target.value })} />
                            <p className="text-[11px] text-[color:var(--fwm-muted)]">
                              {400 - form.gift_note.length} characters left
                            </p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Who is it for?</Label>
                              <Input value={form.recipient_name}
                                     data-testid="checkout-recipient-name"
                                     onChange={(e) => setForm({ ...form,
                                       recipient_name: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">
                                Their email (we&apos;ll send the note)
                              </Label>
                              <Input type="email" value={form.recipient_email}
                                     data-testid="checkout-recipient-email"
                                     onChange={(e) => setForm({ ...form,
                                       recipient_email: e.target.value })} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-2">
              <div className="fwm-card p-7 space-y-5 sticky top-[164px]">
                <h2 className="fwm-display text-xl">Summary</h2>
                <div className="space-y-2.5 text-sm">
                  <p className="flex justify-between">
                    <span className="text-[color:var(--fwm-muted)]">Items</span>
                    <span className="tabular-nums" data-testid="checkout-subtotal">
                      {money(quote?.subtotal, quote?.currency)}
                    </span>
                  </p>
                  {quote?.discount_total > 0 && (
                    <p className="flex justify-between font-medium text-[color:var(--fwm-navy)]"
                       data-testid="checkout-discount">
                      <span>Code {quote.discount?.code}</span>
                      <span>− {money(quote.discount_total, quote?.currency)}</span>
                    </p>
                  )}
                  {quote?.member_savings > 0 && (
                    <p className="flex justify-between font-medium
                                  text-[color:var(--fwm-accent-dark)]"
                       data-testid="checkout-member-savings">
                      <span>Ministry pricing</span>
                      <span>− {money(quote.member_savings, quote?.currency)}</span>
                    </p>
                  )}
                  <p className="flex justify-between">
                    <span className="text-[color:var(--fwm-muted)]">Shipping</span>
                    <span className="tabular-nums">
                      {money(quote?.shipping_fee, quote?.currency)}
                    </span>
                  </p>
                  {quote?.gift_wrap_fee > 0 && (
                    <p className="flex justify-between" data-testid="checkout-gift-wrap-fee">
                      <span className="text-[color:var(--fwm-muted)]">Gift wrapping</span>
                      <span className="tabular-nums">
                        {money(quote.gift_wrap_fee, quote?.currency)}
                      </span>
                    </p>
                  )}
                  <p className="flex justify-between items-baseline pt-3
                                border-t border-[color:var(--fwm-line)]">
                    <span className="font-semibold">Total</span>
                    <span className="fwm-price" data-testid="checkout-total">
                      {money(quote?.total, quote?.currency)}
                    </span>
                  </p>
                </div>

                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs">Discount code</Label>
                  <Input value={form.discount_code} data-testid="checkout-discount-code"
                         onChange={(e) => setForm({ ...form, discount_code: e.target.value })}
                         placeholder="Have a code?" />
                  {form.discount_code && quote?.discount && !quote.discount.ok
                    && quote.discount.reason && (
                    <p className="text-[11px] text-red-600" data-testid="checkout-discount-error">
                      {quote.discount.reason}
                    </p>
                  )}
                </div>

                <div className="space-y-2 pt-1">
                  <Label className="text-xs">How would you like to pay?</Label>
                  {methods.map(([key, label]) => (
                    <label key={key}
                           className={`flex items-center gap-3 rounded-[3px] border p-3 text-sm
                             cursor-pointer transition-colors ${form.payment_method === key
                               ? 'border-[color:var(--fwm-accent)] bg-[color:var(--fwm-ground-2)]'
                               : 'border-[color:var(--fwm-line)] hover:border-[color:var(--fwm-strong)]'}`}>
                      <input type="radio" name="pay" checked={form.payment_method === key}
                             data-testid={`checkout-pay-${key}`}
                             className="accent-[color:var(--fwm-accent)]"
                             onChange={() => setForm({ ...form, payment_method: key })} />
                      {label}
                    </label>
                  ))}
                  {form.payment_method === 'paynow' && (
                    <div className="space-y-2 pl-1">
                      {(settings?.wallets || []).map((w) => (
                        <label key={w.key} className="flex items-center gap-3 text-sm">
                          <input type="radio" name="wallet" checked={form.wallet === w.key}
                                 data-testid={`checkout-wallet-${w.key}`}
                                 className="accent-[color:var(--fwm-accent)]"
                                 onChange={() => setForm({ ...form, wallet: w.key })} />
                          {w.label}
                        </label>
                      ))}
                    </div>
                  )}
                  {form.payment_method === 'bank_transfer' && settings?.bank_details && (
                    <p className="text-xs text-[color:var(--fwm-body)] whitespace-pre-line
                                  rounded-[3px] border border-[color:var(--fwm-line)] p-3">
                      {settings.bank_details}
                    </p>
                  )}
                  {methods.length === 0 && (
                    <p className="text-xs text-red-600" data-testid="checkout-no-methods">
                      No payment method is switched on yet — please contact the office.
                    </p>
                  )}
                </div>

                <button onClick={place} disabled={busy || methods.length === 0}
                        data-testid="checkout-place-order"
                        className="fwm-btn fwm-btn-lg w-full !justify-center
                                   disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Place the order
                </button>
                <p className="text-[11px] text-[color:var(--fwm-muted)] leading-relaxed">
                  No account needed. Members get ministry pricing automatically when the email
                  matches their church record.
                </p>
              </div>
            </aside>
          </div>
        )}
      </main>
    </ShopShell>
  );
}

export function ShopOrderPage() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const basket = useBasket();
  const email = params.get('email') || localStorage.getItem(EMAIL_KEY) || '';

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['shop-order', orderNumber, email],
    queryFn: () => fetch(`${API}/public/orders/${orderNumber}?email=${encodeURIComponent(email)}`)
      .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); }),
    refetchInterval: (query) => (query.state.data?.payment_status === 'pending' ? 5000 : false),
    retry: false,
  });

  if (isLoading) {
    return (
      <ShopShell basketCount={basket.count} testId="shop-order-loading">
        <div className="fwm-section">
          <div className="h-60 rounded-xl bg-[color:var(--fwm-ground-2)] animate-pulse" />
        </div>
      </ShopShell>
    );
  }
  if (error || !order) {
    return (
      <ShopShell basketCount={basket.count}>
        <div className="fwm-section text-center" data-testid="shop-order-missing">
          <p className="text-[color:var(--fwm-body)]">We could not find that order.</p>
        </div>
      </ShopShell>
    );
  }

  const paid = order.payment_status === 'paid';

  return (
    <ShopShell basketCount={basket.count}>
      <main className="mx-auto max-w-[760px] px-6 py-20" data-testid="shop-order-page">
        <p className="text-[11px] font-mono text-[color:var(--fwm-muted)]">
          {order.order_number}
        </p>
        <h1 className="fwm-display mt-4 text-4xl tracking-tight">
          {paid ? 'Thank you — payment received' : 'Order placed, waiting on payment'}
        </h1>
        <span className={`mt-6 inline-flex items-center ${paid ? 'fwm-badge-solid' : 'fwm-badge'}`}
              data-testid="shop-order-status">
          {paid ? <Check className="h-3 w-3 mr-1" /> : null}
          {order.payment_status} · {order.status}
        </span>

        <div className="mt-12 fwm-card divide-y divide-[color:var(--fwm-line)]">
          {(order.items || []).map((line) => (
            <div key={line.product_id} className="p-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">{line.name}</p>
                <p className="text-xs text-[color:var(--fwm-muted)]">
                  {line.kind === 'digital' ? 'Digital' : `Qty ${line.qty}`}
                </p>
              </div>
              <p className="font-semibold tabular-nums">
                {money(line.line_total, order.currency)}
              </p>
            </div>
          ))}
          <div className="p-5 flex items-center justify-between">
            <span className="font-semibold">Total</span>
            <span className="fwm-price" data-testid="shop-order-total">
              {money(order.total, order.currency)}
            </span>
          </div>
        </div>

        {(order.downloads || []).length > 0 && (
          <div className="mt-12" data-testid="shop-order-downloads">
            <h2 className="fwm-display text-xl">Your downloads</h2>
            <div className="mt-5 space-y-3">
              {order.downloads.map((d) => (
                <a key={d.url} href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                   data-testid={`shop-download-${d.name}`}
                   className="fwm-card flex items-center justify-between p-5
                              hover:border-[color:var(--fwm-accent)] transition-colors">
                  <span className="font-medium">{d.name}</span>
                  <Download className="h-4 w-4 text-[color:var(--fwm-accent-dark)]" />
                </a>
              ))}
            </div>
          </div>
        )}

        {!paid && (
          <p className="mt-10 text-sm text-[color:var(--fwm-muted)]"
             data-testid="shop-order-pending-note">
            We will confirm as soon as the payment lands. This page updates itself.
          </p>
        )}
      </main>
    </ShopShell>
  );
}
